import { StorageService, StorageKey } from '../storage/storage.service';
import { GameId } from 'src/app/model/game/game-id';

interface BaseDeposit {
  id: string;
  gameId: GameId;
  tierId: string;
  roomId?: string;
  tournamentId?: string;
  checkCount: number;
  canCheckAt: number;
  expiresAt: number;
}

export interface LightningDeposit extends BaseDeposit {
  amountSats: number;
  invoice: string;
}

export type Deposit = LightningDeposit;

export function isDepositExpired(deposit: Deposit): boolean {
  return deposit.expiresAt < Date.now();
}

export function canCheckDeposit(deposit: Deposit): boolean {
  return deposit.canCheckAt < Date.now();
}

export class Deposits {
  private deposits: Deposit[] = [];
  private initializing: Promise<void> | null = null;

  constructor(
    private userId: string,
    private storageService: StorageService,
  ) {}

  async initialise(): Promise<void> {
    if (this.initializing) return this.initializing;

    this.initializing = (async () => {
      const storedDeposits = await this.storageService.getItem<Deposit[]>(this.key());
      this.deposits = storedDeposits || [];
      this.initializing = null;
    })();

    return this.initializing;
  }

  async add(deposit: Deposit): Promise<void> {
    await this.isInitialized();
    this.deposits.push(deposit);
    await this.storageService.setItem(this.key(), this.deposits);
  }

  async remove(deposit: Deposit): Promise<void> {
    await this.isInitialized();
    this.deposits = this.deposits.filter((d) => d.id !== deposit.id);
    await this.storageService.setItem(this.key(), this.deposits);
  }

  async removeInvoice(invoice: string): Promise<void> {
    await this.isInitialized();
    this.deposits = this.deposits.filter((d) => {
      return d.invoice !== invoice;
    });
    await this.storageService.setItem(this.key(), this.deposits);
  }

  getDeposits(): Deposit[] {
    return [...this.deposits];
  }

  async checked(deposit: Deposit): Promise<void> {
    await this.isInitialized();
    const foundDeposit = this.deposits.find((d) => d == deposit);
    if (foundDeposit) {
      foundDeposit.checkCount += 1;
      const delay = 1000 * Math.pow(2, Math.min(Math.max(0, foundDeposit.checkCount - 30), 3));
      foundDeposit.canCheckAt = Date.now() + delay;
      await this.storageService.setItem(this.key(), this.deposits);
    }
  }

  private key(): string {
    return `${this.userId}_${StorageKey.Deposits}`;
  }

  private async isInitialized(): Promise<void> {
    if (this.initializing) {
      await this.initializing;
    }
  }
}
